/**
 * TCG deck pack/unpack and client-side legality helpers.
 * packedTeam for TCG formats is a JSON array of card ids (server parseTcgDeck).
 *
 * Do not define a local `toID` — babel strips imports to globals, and a local
 * `toID` would overwrite Dex's and break PSIcon / getPokemonIcon.
 */

import { toID } from "./battle-dex";
import { Net } from "./client-connection";

export type TcgCardRow = {
	id: string,
	n: string,
	/** P / T / E */
	s: string,
	u: string[],
	t: string[],
	set: string,
	r: string,
	/** p = paper, k = pocket */
	k: string,
	i: string,
	std: number,
	pkt: number,
	b: number,
	be: number,
	ace: number,
	rad: number,
	prism: number,
	/** Name this Pokémon evolves from (omitted for Basics). */
	ef?: string,
	/** Energy types its attack costs name, other than Colorless (omitted when none). */
	ec?: string[],
};

export type TcgDeckFormat = 'standard' | 'pocket' | 'custom';

export type TcgDeckRules = {
	id: TcgDeckFormat,
	minDeckSize?: number,
	name: string,
	deckSize: number,
	copyLimit: number,
	energyZone: boolean,
	allowAceSpec: boolean,
	allowRadiant: boolean,
	/** Showdown format id prefix without gen */
	psFormat: string,
};

export const TCG_DECK_RULES: { [id in TcgDeckFormat]: TcgDeckRules } = {
	standard: {
		id: 'standard',
		name: 'Standard',
		deckSize: 60,
		copyLimit: 4,
		energyZone: false,
		allowAceSpec: true,
		allowRadiant: true,
		psFormat: 'tcgstandard',
	},
	pocket: {
		id: 'pocket',
		name: 'Pocket',
		deckSize: 20,
		copyLimit: 2,
		energyZone: true,
		allowAceSpec: false,
		allowRadiant: false,
		psFormat: 'tcgpocket',
	},
	custom: {
		id: 'custom',
		name: 'Custom Game',
		minDeckSize: 1,
		deckSize: 60,
		copyLimit: 60,
		energyZone: false,
		allowAceSpec: true,
		allowRadiant: true,
		psFormat: 'tcgcustomgame',
	},
};

let cardIndex: TcgCardRow[] | null = null;
let byId: { [id: string]: TcgCardRow } | null = null;
let byIdLower: { [id: string]: TcgCardRow } | null = null;
let byName: { [name: string]: TcgCardRow[] } | null = null;
let loadPromise: Promise<TcgCardRow[]> | null = null;

function indexCards(rows: TcgCardRow[]) {
	cardIndex = rows;
	byId = Object.create(null);
	byIdLower = Object.create(null);
	byName = Object.create(null);
	for (const c of rows) {
		byId[c.id] = c;
		byIdLower[c.id.toLowerCase()] = c;
		const key = toID(c.n);
		const list = byName[key] || [];
		list.push(c);
		byName[key] = list;
	}
}

export function isTcgFormatId(format: string | undefined | null): boolean {
	const id = toID(format || '');
	return id.replace(/^gen\d/, '').startsWith('tcg');
}

export function isTcgRandomFormat(format: string | undefined | null): boolean {
	const id = toID(format || '');
	return id.includes('tcg') && id.includes('random');
}

export function tcgDeckFormatOf(format: string | undefined | null): TcgDeckFormat {
	const id = toID(format || '');
	if (id.includes('pocket')) return 'pocket';
	if (id.includes('custom')) return 'custom';
	return 'standard';
}

export function showdownFormatFor(rules: TcgDeckFormat): string {
	const needle = TCG_DECK_RULES[rules].psFormat;
	const formats = typeof BattleFormats !== 'undefined' ? BattleFormats : null;
	if (formats) {
		const hit = Object.keys(formats).find(id => {
			const bare = id.replace(/^gen\d/, '');
			return bare === needle && !id.includes('random');
		});
		if (hit) return hit;
	}
	return needle;
}

export function packTcgDeck(ids: string[]): string {
	return JSON.stringify(ids.filter(Boolean));
}

export function unpackTcgDeck(packed: string | undefined | null): string[] {
	const s = String(packed || '').trim();
	if (!s) return [];
	if (s.startsWith('[')) {
		try {
			const v = JSON.parse(s) as unknown;
			if (!Array.isArray(v)) return [];
			return v.filter((x): x is string => typeof x === 'string' && !!x);
		} catch {
			return [];
		}
	}
	if (s.includes('|')) return [];
	return s.split(/[\s,]+/).filter(Boolean);
}

export function isTcgPacked(packed: string | undefined | null): boolean {
	const s = String(packed || '').trim();
	if (!s) return true;
	if (s.startsWith('[')) {
		try {
			const v = JSON.parse(s);
			return Array.isArray(v);
		} catch {
			return false;
		}
	}
	return !s.includes('|');
}

export function loadTcgCardIndex(): Promise<TcgCardRow[]> {
	if (cardIndex) return Promise.resolve(cardIndex);
	if (loadPromise) return loadPromise;
	// The card index ships with this client. Net would send a root path to the sprite host.
	const indexUrl = (typeof document !== 'undefined' && document.location?.host) ?
		document.location.protocol + '//' + document.location.host + '/tcg-cards-index.json' :
		'/tcg-cards-index.json';
	loadPromise = Net(indexUrl).get()
		.then(text => {
			const rows = JSON.parse(text) as TcgCardRow[];
			if (!Array.isArray(rows)) throw new Error('tcg-cards-index.json');
			indexCards(rows);
			return rows;
		})
		.catch(err => {
			loadPromise = null;
			console.warn(err);
			indexCards([]);
			return cardIndex!;
		});
	return loadPromise;
}

export function getTcgCard(id: string): TcgCardRow | undefined {
	if (!id) return undefined;
	return byId?.[id] || byIdLower?.[id.toLowerCase()];
}

export function allTcgCards(): TcgCardRow[] {
	return cardIndex || [];
}

export function cardLegalInFormat(card: TcgCardRow, format: TcgDeckFormat): boolean {
	if (format === 'custom') return true;
	return format === 'pocket' ? !!card.pkt : !!card.std;
}

export function copyMaxFor(card: TcgCardRow, format: TcgDeckFormat): number {
	if (card.be) return 99;
	if (format === 'custom') return TCG_DECK_RULES[format].copyLimit;
	const rules = TCG_DECK_RULES[format];
	let max = rules.copyLimit;
	if (card.ace || card.rad || card.prism) max = Math.min(max, 1);
	return max;
}

export type TcgDeckLine = { id: string, card: TcgCardRow | undefined, n: number };

export function groupDeck(ids: string[]): TcgDeckLine[] {
	const order: string[] = [];
	const counts: { [id: string]: number } = Object.create(null);
	for (const id of ids) {
		if (!counts[id]) order.push(id);
		counts[id] = (counts[id] || 0) + 1;
	}
	return order.map(id => ({ id, card: getTcgCard(id), n: counts[id] }));
}

export function deckProblemsClient(ids: string[], format: TcgDeckFormat): string[] {
	const rules = TCG_DECK_RULES[format];
	const errors: string[] = [];
	if (format === 'custom') {
		const min = rules.minDeckSize || 1;
		if (ids.length < min || ids.length > rules.deckSize) {
			errors.push(`${rules.name} decks must have ${min}–${rules.deckSize} cards (got ${ids.length}).`);
		}
		for (const id of ids) {
			if (!getTcgCard(id)) errors.push(`Unknown card ${id}.`);
		}
		return errors;
	}
	if (ids.length !== rules.deckSize) {
		errors.push(`${rules.name} decks must be ${rules.deckSize} cards (got ${ids.length}).`);
	}
	const named: { [name: string]: number } = Object.create(null);
	let ace = 0;
	let radiant = 0;
	let basics = 0;
	let energyCards = 0;
	const illegal: string[] = [];
	const over: string[] = [];

	for (const id of ids) {
		const c = getTcgCard(id);
		if (!c) {
			errors.push(`Unknown card ${id}.`);
			continue;
		}
		if (!cardLegalInFormat(c, format)) {
			illegal.push(`${c.n} (${c.id}${c.r ? `, ${c.r}` : ''})`);
		}
		if (c.b) basics++;
		if (c.ace) ace++;
		if (c.rad) radiant++;
		if (rules.energyZone && c.s === 'E') {
			energyCards++;
			continue;
		}
		if (c.be) continue;
		const n = (named[c.n] || 0) + 1;
		named[c.n] = n;
		if (n === copyMaxFor(c, format) + 1) over.push(`${c.n} (max ${copyMaxFor(c, format)})`);
	}

	if (illegal.length) {
		errors.push(`Not legal in ${rules.name}: ${illegal.slice(0, 6).join(', ')}${illegal.length > 6 ? '…' : ''}`);
	}
	if (energyCards) errors.push(`${rules.name} decks do not include Energy cards (Energy Zone).`);
	if (over.length) errors.push(`Too many copies: ${over.slice(0, 6).join(', ')}${over.length > 6 ? '…' : ''}`);
	if (!rules.allowAceSpec && ace) errors.push(`${rules.name} does not allow ACE SPEC.`);
	else if (ace > 1) errors.push('Only 1 ACE SPEC card per deck.');
	if (!rules.allowRadiant && radiant) errors.push(`${rules.name} does not allow Radiant Pokemon.`);
	else if (radiant > 1) errors.push('Only 1 Radiant Pokemon per deck.');
	if (ids.length && !basics) errors.push('Deck needs at least one Basic Pokemon.');
	return errors;
}

export function deckCounts(ids: string[]) {
	let pokemon = 0, trainer = 0, energy = 0;
	for (const id of ids) {
		const c = getTcgCard(id);
		if (!c) continue;
		if (c.s === 'P') pokemon++;
		else if (c.s === 'T') trainer++;
		else if (c.s === 'E') energy++;
	}
	return { pokemon, trainer, energy, total: ids.length };
}

export function deckPreviewCards(ids: string[], limit = 6): TcgCardRow[] {
	const seen: { [id: string]: 1 } = Object.create(null);
	const poke: TcgCardRow[] = [];
	const other: TcgCardRow[] = [];
	for (const id of ids) {
		if (seen[id]) continue;
		seen[id] = 1;
		const c = getTcgCard(id);
		if (!c) continue;
		if (c.s === 'P') poke.push(c);
		else other.push(c);
	}
	return poke.concat(other).slice(0, limit);
}

const SECTION_LABEL: { [s: string]: string } = {
	P: 'Pokémon',
	T: 'Trainer',
	E: 'Energy',
};

export function exportTcgDeck(ids: string[]): string {
	const lines = groupDeck(ids);
	const out: string[] = [];
	const pushGroup = (label: string, group: TcgDeckLine[]) => {
		let count = 0;
		for (const line of group) count += line.n;
		out.push(`${label}: ${count}`);
		for (const line of group) out.push(`${line.n} ${line.card?.n || line.id} ${line.id}`);
		out.push('');
	};
	const sections = ['P', 'T', 'E'];
	for (const section of sections) {
		const group: TcgDeckLine[] = [];
		for (const line of lines) {
			if ((line.card?.s || '?') === section) group.push(line);
		}
		pushGroup(SECTION_LABEL[section], group);
	}
	const unknown: TcgDeckLine[] = [];
	for (const line of lines) {
		if (!line.card || !SECTION_LABEL[line.card.s]) unknown.push(line);
	}
	if (unknown.length) pushGroup('Other', unknown);
	return out.join('\n').replace(/\s+$/, '') + '\n';
}

function preferLegal(cards: TcgCardRow[], format: TcgDeckFormat): TcgCardRow[] {
	const legal = cards.filter(c => cardLegalInFormat(c, format) || (format === 'standard' && c.be));
	return legal.length ? legal : cards;
}

/** Resolve a pasted card token to a catalog id. */
export function resolveImportCard(
	rest: string,
	format: TcgDeckFormat
): { id: string, warning?: string } | null {
	rest = rest.trim().replace(/\s+/g, ' ');
	if (!rest) return null;

	// Bare card id
	const direct = getTcgCard(rest);
	if (direct) return { id: direct.id };

	const parts = rest.split(' ');
	// Trailing card id: "Abomasnow sv10-60"
	if (parts.length >= 2) {
		const last = parts[parts.length - 1];
		const byTail = getTcgCard(last);
		if (byTail) return { id: byTail.id };

		// Trailing SET NUM: "Charizard ex OBF 125" → obf-125
		if (parts.length >= 3) {
			const set = parts[parts.length - 2];
			const num = parts[parts.length - 1];
			if (/^[A-Za-z0-9]+$/.test(set) && /^[A-Za-z0-9]+$/.test(num)) {
				const candidates = [
					`${set}-${num}`,
					`${set.toLowerCase()}-${num}`,
					`${set}-${num.replace(/^0+/, '') || '0'}`,
					`${set.toLowerCase()}-${(num.replace(/^0+/, '') || '0')}`,
					`${set}-${('000' + num).slice(-3)}`,
				];
				for (const id of candidates) {
					const c = getTcgCard(id);
					if (c) return { id: c.id };
				}
			}
		}
	}

	// Name only (optionally with ignored trailing set tokens that failed above)
	let name = rest;
	if (parts.length >= 3 &&
		/^[A-Za-z0-9]+$/.test(parts[parts.length - 2]) &&
		/^[A-Za-z0-9]+$/.test(parts[parts.length - 1])) {
		name = parts.slice(0, -2).join(' ');
	} else if (parts.length >= 2 && getTcgCard(parts[parts.length - 1])) {
		name = parts.slice(0, -1).join(' ');
	}

	const matches = preferLegal(byName?.[toID(name)] || [], format);
	if (!matches.length) return null;
	if (matches.length === 1) return { id: matches[0].id };
	// Prefer higher regulation mark, then stable id order
	matches.sort((a, b) => (b.r || '').localeCompare(a.r || '') || a.id.localeCompare(b.id));
	return {
		id: matches[0].id,
		warning: `Multiple prints of ${matches[0].n}; using ${matches[0].id}`,
	};
}

/** Basic Energy prints by type. Fairy and Dragon have no Basic Energy in Standard. */
export const TCG_BASIC_ENERGY: { [type: string]: string } = {
	grass: 'sve-1',
	fire: 'sve-2',
	water: 'sve-3',
	lightning: 'sve-4',
	psychic: 'sve-5',
	fighting: 'sve-6',
	darkness: 'sve-7',
	metal: 'sve-8',
};

type FillStaple = {
	id: string,
	/** Copies the deck should hold, counting copies already in it. */
	n: number,
	/** Only when the deck has a Stage 2 Pokémon. */
	stage2?: boolean,
	/** Only when the deck has an Evolution Pokémon. */
	evolution?: boolean,
};

/**
 * Type-neutral Trainers from the Standard Random and Pocket Random tournament lists,
 * in the order the filler adds them. Every id is legal and has a card script.
 */
const FILL_TRAINERS: { [id in TcgDeckFormat]: FillStaple[] } = {
	standard: [
		{ id: 'me1-119', n: 4 }, // Lillie's Determination
		{ id: 'me1-131', n: 4 }, // Ultra Ball
		{ id: 'me1-114', n: 3 }, // Boss's Orders
		{ id: 'me1-125', n: 3, stage2: true }, // Rare Candy
		{ id: 'rsv10pt5-84', n: 2, evolution: true }, // Hilda
		{ id: 'me2pt5-196', n: 2 }, // Night Stretcher
		{ id: 'me3-81', n: 2 }, // Poké Pad
		{ id: 'zsv10pt5-84', n: 2 }, // Pokégear 3.0
		{ id: 'me1-130', n: 2 }, // Switch
		{ id: 'me2pt5-181', n: 2 }, // Air Balloon
		{ id: 'me3-76', n: 1 }, // Judge
	],
	pocket: [
		{ id: 'P-A-007', n: 2 }, // Professor's Research
		{ id: 'P-A-005', n: 2 }, // Poké Ball
		{ id: 'A3-144', n: 2, stage2: true }, // Rare Candy
		{ id: 'B1-225', n: 1 }, // Copycat
		{ id: 'A1-225', n: 1 }, // Sabrina
		{ id: 'A2-150', n: 1 }, // Cyrus
		{ id: 'P-A-002', n: 1 }, // X Speed
		{ id: 'A2-146', n: 1 }, // Pokémon Communication
		{ id: 'B3-147', n: 1 }, // Field Blower
		{ id: 'A2b-070', n: 1 }, // Pokémon Center Lady
		{ id: 'A1-223', n: 1 }, // Giovanni
		{ id: 'P-A-006', n: 1 }, // Red Card
		{ id: 'B2-145', n: 1 }, // Lucky Ice Pop
		{ id: 'P-A-001', n: 1 }, // Potion
	],
	custom: [
		{ id: 'me1-119', n: 4 }, // Lillie's Determination
		{ id: 'me1-131', n: 4 }, // Ultra Ball
		{ id: 'me1-114', n: 3 }, // Boss's Orders
		{ id: 'me1-125', n: 3, stage2: true }, // Rare Candy
		{ id: 'rsv10pt5-84', n: 2, evolution: true }, // Hilda
		{ id: 'me2pt5-196', n: 2 }, // Night Stretcher
		{ id: 'me3-81', n: 2 }, // Poké Pad
		{ id: 'zsv10pt5-84', n: 2 }, // Pokégear 3.0
		{ id: 'me1-130', n: 2 }, // Switch
		{ id: 'me2pt5-181', n: 2 }, // Air Balloon
		{ id: 'me3-76', n: 1 }, // Judge
	],
};

/** Support Pokémon that fit any Standard deck (each in half of the Standard Random lists). */
const FILL_SUPPORT: { [id in TcgDeckFormat]: string[] } = {
	standard: ['me2pt5-142', 'me3-62'], // Fezandipiti ex, Meowth ex
	pocket: [],
	custom: ['me2pt5-142', 'me3-62'],
};

const FILL_POKEMON_TARGET: { [id in TcgDeckFormat]: number } = { standard: 12, pocket: 8, custom: 12 };
const FILL_ENERGY_TARGET = 10;

export type TcgFillResult = {
	ids: string[],
	/** Cards the filler added, grouped like a deck list. */
	added: TcgDeckLine[],
	notes: string[],
};

function stageOf(card: TcgCardRow): number {
	if (card.u.includes('Stage 2')) return 2;
	return card.ef ? 1 : 0;
}

/**
 * Complete a partial deck around the cards the player chose. Keeps every chosen card, then adds
 * the missing pre-evolutions of each Evolution Pokémon, more copies of the chosen Pokémon,
 * type-neutral Trainers and (Standard only) Basic Energy for the attack costs. Deterministic.
 */
export function fillTcgDeck(ids: string[], format: TcgDeckFormat): TcgFillResult {
	const rules = TCG_DECK_RULES[format];
	const out = ids.slice();
	const notes: string[] = [];
	const room = () => rules.deckSize - out.length;
	const done = (): TcgFillResult => ({ ids: out, added: groupDeck(out.slice(ids.length)), notes });
	if (room() <= 0) {
		notes.push(`The deck already has ${out.length} cards.`);
		return done();
	}
	const deckCards = () => out.map(id => getTcgCard(id)).filter((c): c is TcgCardRow => !!c);
	const pokemon = () => deckCards().filter(c => c.s === 'P');
	if (!pokemon().length) {
		notes.push('Add at least one Pokémon, then fill the rest.');
		return done();
	}

	const countNamed = (name: string) => deckCards().filter(c => c.n === name && !c.be).length;
	const legal = (c: TcgCardRow) => cardLegalInFormat(c, format) && !(rules.energyZone && c.s === 'E');
	const canAdd = (c: TcgCardRow) => {
		if (room() <= 0 || !legal(c)) return false;
		if (c.be) return true;
		if (countNamed(c.n) >= copyMaxFor(c, format)) return false;
		if (c.ace && deckCards().some(x => x.ace)) return false;
		if (c.rad && deckCards().some(x => x.rad)) return false;
		return true;
	};
	/** Add copies of a card until the deck holds `want` cards with its name. */
	const fillTo = (c: TcgCardRow, want: number) => {
		while (countNamed(c.n) < want && canAdd(c)) out.push(c.id);
	};

	// 1. Pre-evolutions, Stage 2 first so the Stage 1 it adds gets its Basic in the next pass.
	// Each pre-evolution gets one more copy than the cards that evolve from it.
	for (const stage of [2, 1]) {
		const preNames: string[] = [];
		for (const c of pokemon()) {
			if (c.ef && stageOf(c) === stage && !preNames.includes(c.ef)) preNames.push(c.ef);
		}
		preNames.forEach(preName => {
			const evolutions = pokemon().filter(c => c.ef === preName);
			const owned = deckCards().find(c => c.n === preName);
			const types = evolutions[0].t;
			const pick = owned || (byName?.[toID(preName)] || [])
				.filter(c => c.n === preName && legal(c))
				.sort((a, b) =>
					Number(b.t.some(t => types.includes(t))) - Number(a.t.some(t => types.includes(t))) ||
					(b.r || '').localeCompare(a.r || '') || a.id.localeCompare(b.id))[0];
			if (!pick || !legal(pick)) {
				notes.push(`No ${rules.name}-legal ${preName} for ${evolutions[0].n}.`);
				return;
			}
			fillTo(pick, Math.min(copyMaxFor(pick, format), evolutions.length + 1));
		});
	}

	// 2. Support Pokémon, then more copies of the chosen Pokémon: Basics first, and never more
	// copies of an Evolution than of what it evolves from.
	const target = Math.min(FILL_POKEMON_TARGET[format], rules.deckSize);
	for (const id of FILL_SUPPORT[format]) {
		const c = getTcgCard(id);
		if (c && pokemon().length < target) fillTo(c, 1);
	}
	const lines = pokemon()
		.filter((c, i, all) => all.findIndex(x => x.n === c.n) === i && !FILL_SUPPORT[format].includes(c.id))
		.sort((a, b) => stageOf(a) - stageOf(b));
	for (let grew = true; grew && pokemon().length < target;) {
		grew = false;
		for (const c of lines) {
			if (pokemon().length >= target) break;
			const have = countNamed(c.n);
			if (c.ef && have >= countNamed(c.ef)) continue;
			fillTo(c, have + 1);
			if (countNamed(c.n) > have) grew = true;
		}
	}

	// 3. Trainers, leaving room for Energy in Standard.
	const energyN = () => deckCards().filter(c => c.s === 'E').length;
	const reserve = () => (rules.energyZone ? 0 : Math.max(0, FILL_ENERGY_TARGET - energyN()));
	const hasStage2 = pokemon().some(c => stageOf(c) === 2);
	const hasEvolution = pokemon().some(c => c.ef);
	const staples = FILL_TRAINERS[format].filter(s => (!s.stage2 || hasStage2) && (!s.evolution || hasEvolution));
	// A second pass raises each staple to its copy limit when the first pass leaves room.
	for (const pass of [0, 1]) {
		for (const s of staples) {
			const c = getTcgCard(s.id);
			if (!c || room() <= reserve()) break;
			const want = pass ? copyMaxFor(c, format) : s.n;
			while (countNamed(c.n) < want && room() > reserve() && canAdd(c)) out.push(c.id);
		}
	}

	// 4. Basic Energy for the attack costs, split by how many Pokémon need each type.
	if (!rules.energyZone && room() > 0) {
		const weight: { [type: string]: number } = Object.create(null);
		for (const c of pokemon()) {
			for (const t of c.ec || []) if (TCG_BASIC_ENERGY[t]) weight[t] = (weight[t] || 0) + 1;
		}
		if (!Object.keys(weight).length) {
			for (const c of pokemon()) for (const t of c.t) if (TCG_BASIC_ENERGY[t]) weight[t] = (weight[t] || 0) + 1;
		}
		if (!Object.keys(weight).length) {
			weight.grass = 1;
			notes.push('The attacks only need Colorless Energy, so Grass Energy was added. Any Basic Energy works.');
		}
		const types = Object.keys(weight).sort((a, b) => weight[b] - weight[a] || a.localeCompare(b));
		const total = types.reduce((n, t) => n + weight[t], 0);
		const count = room();
		// Largest remainder, so the split adds up to exactly `count`.
		const share = types.map(t => ({ t, exact: (count * weight[t]) / total }));
		const n = share.map(s => Math.floor(s.exact));
		let left = count - n.reduce((a, b) => a + b, 0);
		const byRemainder = share.map((_s, i) => i).sort((a, b) =>
			(share[b].exact - Math.floor(share[b].exact)) - (share[a].exact - Math.floor(share[a].exact)) || a - b);
		for (const i of byRemainder) {
			if (left <= 0) break;
			n[i]++;
			left--;
		}
		types.forEach((t, i) => {
			for (let k = 0; k < n[i]; k++) out.push(TCG_BASIC_ENERGY[t]);
		});
	}

	const poke = pokemon().length;
	// A Fossil line starts from an Item, so the deck can still lack a Basic to open with.
	if (!pokemon().some(c => c.b)) notes.push('No Basic Pokémon yet. Add one so the deck can start a game.');
	if (poke < target) notes.push(`Only ${poke} Pokémon. Add more Pokémon for a steadier deck.`);
	if (room() > 0) notes.push(`Could not find enough cards to fill the last ${room()}.`);
	return done();
}

export type TcgImportResult = {
	ids: string[],
	errors: string[],
	warnings: string[],
};

/** Parse Limitless-style text, JSON id arrays, or bare card-id lists. */
export function importTcgDeck(text: string, format: TcgDeckFormat): TcgImportResult {
	const trimmed = String(text || '').trim();
	if (!trimmed) return { ids: [], errors: [], warnings: [] };

	if (trimmed.startsWith('[')) {
		const ids = unpackTcgDeck(trimmed);
		const errors: string[] = [];
		for (const id of ids) {
			if (!getTcgCard(id)) errors.push(`Unknown card id: ${id}`);
		}
		return { ids, errors, warnings: [] };
	}

	const ids: string[] = [];
	const errors: string[] = [];
	const warnings: string[] = [];

	for (const raw of trimmed.split(/\r?\n/)) {
		let line = raw.trim();
		if (!line) continue;
		if (/^(pok[eé]mon|trainer|energy|other)\s*:/i.test(line)) continue;
		if (line.startsWith("===") || /^format\s*:/i.test(line) || /^total\s*:/i.test(line)) continue;
		// Strip leading bullets / dashes
		line = line.replace(/^[-*•]\s*/, '');

		let qty = 1;
		let rest = line;
		const qm = /^(\d+)x?\s+(.+)$/i.exec(line);
		if (qm) {
			qty = Math.max(1, Math.min(99, parseInt(qm[1], 10) || 1));
			rest = qm[2].trim();
		} else if (/^[A-Za-z0-9][\w.-]*-\w+$/.test(line) || getTcgCard(line)) {
			rest = line;
		} else if (!/\s/.test(line)) {
			// single token that isn't an id — try as name with qty 1
			rest = line;
		}

		const resolved = resolveImportCard(rest, format);
		if (!resolved) {
			errors.push(`Could not find card: ${rest}`);
			continue;
		}
		if (resolved.warning) warnings.push(resolved.warning);
		for (let i = 0; i < qty; i++) ids.push(resolved.id);
	}

	return { ids, errors, warnings };
}
